import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/utils";

/**
 * Class recipe for `IconButton`. Ported from MindstoneRebel's
 * `IconButton.module.css`: slate-tinted neutral fills become
 * `muted-foreground` at low opacity, the indigo/violet hover and pressed
 * states become `primary` at low opacity, and Rebel's resting `opacity: 0.78`
 * is replaced by a `text-muted-foreground → text-foreground` colour shift so
 * the icon keeps full contrast against its own fill.
 */
const iconButtonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center border text-muted-foreground transition-[color,background-color,border-color,box-shadow] outline-hidden enabled:hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      size: {
        xs: "size-7 rounded-lg",
        sm: "size-8 rounded-lg",
        md: "size-9 rounded-[10px]",
        lg: "size-10 rounded-xl",
      },
      variant: {
        framed:
          "border-muted-foreground/20 bg-muted-foreground/10 enabled:hover:border-primary/25 enabled:hover:bg-primary/10",
        ghost:
          "border-transparent bg-transparent enabled:hover:border-transparent enabled:hover:bg-primary/15",
        subtle:
          "border-muted-foreground/25 bg-muted-foreground/15 enabled:hover:border-primary/25 enabled:hover:bg-primary/10",
      },
      active: {
        true: "border-primary/55 bg-primary/20 text-primary enabled:hover:border-primary/70 enabled:hover:bg-primary/25 enabled:hover:text-primary",
        false: "",
      },
      danger: {
        true: "text-destructive enabled:hover:border-destructive/40 enabled:hover:bg-destructive/15 enabled:hover:text-destructive",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "ghost",
        active: true,
        className:
          "border-transparent bg-primary/15 enabled:hover:border-transparent enabled:hover:bg-primary/20",
      },
    ],
    defaultVariants: {
      size: "md",
      variant: "framed",
      active: false,
      danger: false,
    },
  },
);

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof iconButtonVariants>, "active" | "danger"> {
  /** Square footprint: `xs` 28px, `sm` 32px, `md` 36px (default), `lg` 40px. */
  size?: "xs" | "sm" | "md" | "lg";
  /**
   * Resting treatment. `framed` (default) has a faint fill and border,
   * `ghost` has neither until hovered, `subtle` is a slightly stronger fill.
   */
  variant?: "framed" | "ghost" | "subtle";
  /** Pressed / toggled-on appearance (tinted `primary`). Pair with `aria-pressed`. */
  active?: boolean;
  /** Destructive affordance: icon and hover tint use `destructive`. */
  danger?: boolean;
}

/**
 * Square icon-only action button. Stays visually quiet at rest and becomes
 * clearer on hover, focus, `active`, or `danger`. Always pass an `aria-label`
 * because the only content is an icon. Use `IconTile` for a non-interactive
 * category marker.
 *
 * @example
 * <IconButton aria-label="Refresh" variant="ghost" size="lg">
 *   <RefreshCw />
 * </IconButton>
 */
const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      size = "md",
      variant = "framed",
      active = false,
      danger = false,
      type = "button",
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      data-slot="icon-button"
      data-size={size}
      data-variant={variant}
      data-active={active ? "" : undefined}
      data-danger={danger ? "" : undefined}
      className={cn(iconButtonVariants({ size, variant, active, danger }), className)}
      {...props}
    />
  ),
);
IconButton.displayName = "IconButton";

export { IconButton, iconButtonVariants };
